import { Attr, Names, Values } from '@mode-switch/model';
import {
  AreaMarkOptions,
  BarMarkOptions,
  EncField,
  EncMarks,
  EncSeries,
  Encoding,
  LineMarkOptions,
  MarkChannel,
  MarkInterpolation,
  MarkNullTreatment,
  MarkTransform,
  MarkType,
  MarksSummary,
  TextDisplay,
  VariableType,
} from '@mode-vegas/vegas';
import { EncodingHelpers, FieldFormatHelpers, RenderDataModel, getColorMapKeyByField } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { deepCopy } from '@mode/shared/util-js';
import {
  MuzeChannelEncoding,
  MuzeContextInfo,
  MuzeDataSchema,
  MuzeTextDataInfo,
  buildMuzeTextFormatter,
  getMultipleRefId,
  getMuzeTextFormatter,
} from './muze-chart-data';
import { isTextTable } from './muze-chart-format';
import { MuzeLayerFactory } from './muze-types';

export interface MuzeLayer {
  mark: string;
  source: string;
  encoding: Record<string, string | MuzeChannelEncoding | undefined>;
  transition?: { disabled: boolean };
  highlightExactPoint?: boolean;
  transform?: {
    type?: 'stack' | 'group' | 'identity' | 'stack100percent' | undefined;
  };
  interpolate?: 'linear' | 'catmullRom' | 'step' | 'stepAfter' | 'stepBefore' | undefined;
  connectNullData?: boolean;
  nullDataLineStyle?: any;
}
export const DEFAULT_MIN_BAR_WIDTH = 3;

/**
 * Returns layer creation function for specified MarkType.
 *
 * @param {MarkType} markType mark type
 * @returns {Function} layer creation function
 */
function getLayerCreationFunction(
  markType: MarkType
): (
  channel: '*' | 'x' | 'y',
  source: string,
  marks: EncMarks,
  enc: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
) => MuzeLayer {
  let f: (
    channel: '*' | 'x' | 'y',
    source: string,
    marks: EncMarks,
    enc: Encoding,
    fieldFormats: ChartTypes.FieldFormatMap,
    model: RenderDataModel,
    colors: ChartTypes.ChartColors,
    palette: readonly string[]
  ) => MuzeLayer;
  switch (markType) {
    case MarkType.Bar:
      f = makeBarLayer;
      break;
    case MarkType.Line:
      f = makeLineLayer;
      break;
    case MarkType.Area:
      f = makeAreaLayer;
      break;
    case MarkType.Circle:
      f = makeCircleLayer;
      break;
    case MarkType.Shape:
      f = makeShapeLayer;
      break;
    case MarkType.Text:
      f = makeTextLayer;
      break;
    case MarkType.Donut:
      f = makeDonutLayer;
      break;
    case MarkType.Pie:
      f = makePieLayer;
      break;
    default:
      f = makeBarLayer;
  }
  return f;
}

export function makeTabularLayers(
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
): MuzeLayer[] {
  let layers: MuzeLayer[] = [];
  const { marks } = encoding;

  // Make layers from the series, if there are any...
  if (marks.series.length) {
    for (const series of marks.series) {
      if (series.properties.type.value) {
        layers.push(
          getLayerCreationFunction(series.properties.type.value)(
            marks.channel,
            series.field,
            series.properties,
            encoding,
            fieldFormats,
            model,
            colors,
            palette
          )
        );
      }
    }
  }

  // ...or for continuous colors, which aren't supported by areas and lines, convert to bars...
  else if (
    marks.properties.color.values.length &&
    marks.properties.color.values[0].variable.type === VariableType.CONTINUOUS &&
    (marks.properties.type.value === MarkType.Area || marks.properties.type.value === MarkType.Line)
  ) {
    layers.push(makeBarLayer(marks.channel, '', marks.properties, encoding, fieldFormats, model, colors, palette));
  }

  // ...otherwise, make layers from the marks.
  else if (marks.properties.type.value) {
    layers.push(
      getLayerCreationFunction(marks.properties.type.value)(
        marks.channel,
        '',
        marks.properties,
        encoding,
        fieldFormats,
        model,
        colors,
        palette
      )
    );
  }
  return layers;
}

export function makeVerticalLayers(
  model: RenderDataModel,
  encoding: Encoding,
  muzeSchema: MuzeDataSchema,
  layerFactory: MuzeLayerFactory,
  fieldFormats: ChartTypes.FieldFormatMap,
  colors: ChartTypes.ChartColors,
  palette: readonly string[],
  token: string
): MuzeLayer[] {
  let layers: MuzeLayer[] = [];
  const { marks } = encoding;
  for (const series of marks.series) {
    if (series.properties.type.value) {
      layers.push(
        getLayerCreationFunction(series.properties.type.value)(
          marks.channel,
          series.field,
          series.properties,
          encoding,
          fieldFormats,
          model,
          colors,
          palette
        )
      );
    }
  }

  return mergeSharedLayers(encoding, layers, muzeSchema, layerFactory, token);
}

export function makeHorizontalLayers(
  model: RenderDataModel,
  encoding: Encoding,
  muzeSchema: MuzeDataSchema,
  layerFactory: MuzeLayerFactory,
  fieldFormats: ChartTypes.FieldFormatMap,
  colors: ChartTypes.ChartColors,
  palette: readonly string[],
  token: string
): MuzeLayer[] {
  let layers: MuzeLayer[] = [];
  const { marks } = encoding;
  for (const series of marks.series) {
    if (series.properties.type.value) {
      layers.push(
        getLayerCreationFunction(series.properties.type.value)(
          marks.channel,
          series.field,
          series.properties,
          encoding,
          fieldFormats,
          model,
          colors,
          palette
        )
      );
    }
  }
  const mergedLayers = mergeSharedLayers(encoding, layers, muzeSchema, layerFactory, token);
  return mergedLayers;
}

/**
 * Merges any layers that are on a shared axis into a composed layer and registers the composed
 * layer(s) with the layer factory from Muze.
 * @param encoding Encoding
 * @param layers Layers built for each of the series
 * @param muzeSchema Schema describing the data fields
 * @param layerFactory Layer factory from Muze library for registering the composed layer(s)
 * @returns Array of MuzeLayer which includes both the non-shared and shared axis layers
 */
function mergeSharedLayers(
  encoding: Encoding,
  layers: MuzeLayer[],
  muzeSchema: MuzeDataSchema,
  layerFactory: MuzeLayerFactory,
  token: string
): MuzeLayer[] {
  // Tabular layers can not be shared
  if (encoding.marks.channel === '*') {
    return layers;
  }

  const seriesFields =
    encoding.marks.channel === 'x' ? [...encoding.x, ...encoding.x2] : [...encoding.y, ...encoding.y2];
  const secondaryChannel = encoding.marks.channel === 'x' ? 'y' : 'x';
  const secondaryChannelFields = secondaryChannel === 'y' ? encoding.y : encoding.x;

  const groupIds: Array<string> = [];
  const newLayers = layers
    .map((layer) => {
      const seriesField = seriesFields.find((f) => f.id === layer.source);
      if (!seriesField) {
        return layer;
      }
      if (!seriesField.groupId) {
        // If this is not a shared axis series, then we return a layer for all of the
        // secondary axis fields.
        // This has a multiplicative effect on the layers. If there are two fields on the
        // secondary and 4 fields on the primary, it will generate 8 layers.
        const mappedLayers = secondaryChannelFields.map((field) => {
          return { ...layer, encoding: { ...layer.encoding, [secondaryChannel]: field.id } };
        });
        return mappedLayers.length ? mappedLayers : layer;
      }

      if (groupIds.includes(seriesField.groupId)) {
        // We've already processed this group. Return null which we will
        // filter out after.
        return null;
      }
      if (!encoding.groups?.some((g) => g.id === seriesField.groupId)) {
        // This group doesn't exist. This shouldn't happen
        return layer;
      }
      const groupId = seriesField.groupId;
      groupIds.push(groupId);
      // Create a composite layer to replace this layer which includes
      // all of the shared axis layers

      const groupLayers = layers.filter((l) => {
        const layerSeriesField = seriesFields.find((f) => f.id === l.source);
        return layerSeriesField && layerSeriesField.groupId === groupId;
      });
      if (groupLayers.length <= 1) {
        // There is only one group member so no need to compose the layers together.
        // Return a layer for each secondary field (if any) or just the layer itself.
        const mappedLayers = secondaryChannelFields.map((field) => {
          return { ...layer, encoding: { ...layer.encoding, [secondaryChannel]: field.id } };
        });
        return mappedLayers.length ? mappedLayers : layer;
      }

      // We have a shared axis on the primary axis with more than 1 member. We
      // need to compose the layers into a single layer and make the encoding
      // field assignments.
      const channelToIdMap = new Map<string, string>();
      const sourceMap = new Map<string, string>();

      const layerId = `${token}_${groupId}`;
      // Prepare the group layers to be composed into a single layer.
      const preparedLayers = prepareLayersForComposition(encoding, layerId, groupLayers, channelToIdMap, sourceMap);

      // This call to the Muze LayerFactory creates a new type of layer which can then be used in the encoding.
      // While a built-in layer has a mark like 'bar', 'line', etc. Our composed layer will have a mark type of
      // the group id. This will ensure that the mark type is unique.
      layerFactory.composeLayers(layerId, preparedLayers);

      return secondaryChannelFields.length === 0
        ? makeComposedEncodingLayer(channelToIdMap, sourceMap, secondaryChannel, undefined, muzeSchema, layerId)
        : secondaryChannelFields.map((secondaryField) => {
            return makeComposedEncodingLayer(
              channelToIdMap,
              sourceMap,
              secondaryChannel,
              secondaryField,
              muzeSchema,
              layerId
            );
          });
    })
    .flat()
    .filter((l): l is MuzeLayer => l != null);
  return newLayers;
}

function makeComposedEncodingLayer(
  channelToIdMap: Map<string, string>,
  sourceMap: Map<string, string>,
  secondaryChannel: 'x' | 'y',
  secondaryField: EncField | undefined,
  muzeSchema: MuzeDataSchema,
  groupId: string
) {
  // Map the secondary channel to the secondary field
  if (secondaryField?.id !== undefined) {
    channelToIdMap.set(secondaryChannel, secondaryField.id);
  }

  // Build the layer encoding as a single layer which has all of the channels
  // channelToIdMap = { 'x' => 'e4b3', 'y0' => '2e2b', 'y1' => '3e4e', 'color0' => '5e5e' }
  // layerEncoding = {
  //   x: { field: 'e4b3', as: 'discrete'},
  //   y0: { field: '2e2b', as 'continuous'},
  //   y1: { field: '3e4e', as 'continuous'},
  //   color0: { field: '5e5e', as 'discrete'},
  // }
  //
  const layerEncoding = Array.from(channelToIdMap.entries()).reduce((enc, [channel, fieldId]) => {
    return {
      ...enc,
      [channel]: buildFieldConfig(fieldId, muzeSchema),
    };
  }, {});

  // Build the source encoding
  // sourceMap = { 'bar.2e2b' => '2e2b', 'point.3e4e' => '3e4e' }
  // sourceEncoding = { 'bar.2e2b': '2e2b', 'point.3e4e': '3e4e' }
  //
  const sourceEncoding = Array.from(sourceMap.entries()).reduce((sources, [source, channel]) => {
    return {
      ...sources,
      [source]: channel,
    };
  }, {});

  // Return the completed composed layer
  // {
  //   mark: 'GRP0001',
  //   encoding: {
  //     x: { field: 'e4b3', as: 'discrete'},
  //     y0: { field: '2e2b', as 'continuous'},
  //     y1: { field: '3e4e', as 'continuous'},
  //     color0: { field: '5e5e', as 'discrete'},
  //   },
  //   bar.2e2b: '2e2b',
  //   point.3e4e: '3e4e',
  // }
  return {
    mark: groupId,
    encoding: layerEncoding,
    ...sourceEncoding,
  };
}

//
// Returns a new Array of layers which can be used to compose a single output layer.
// We need to remove the explicit field ids from the encoding channels and instead use
// generic identifiers which we can then set to actual fields on the composed layers.
//
function prepareLayersForComposition(
  encoding: Encoding,
  groupId: string,
  groupLayers: MuzeLayer[],
  channelToIdMap: Map<string, string>,
  sourceMap: Map<string, string>
) {
  // Example:
  // Input layers:
  // [{
  //   mark: 'point',
  //   source: 'series1_id',
  //   encoding: {
  //     color: 'color_id',
  //     y: 'series1_id',
  //   },
  // },
  // {
  //   mark: 'bar',
  //   source: 'series2_id',
  //   encoding: {
  //     color: 'color_id',
  //     text: {
  //        field: 'text_id',
  //        rotation: 90,
  //     }
  //     y: 'series2_id',
  //   },
  // }]
  //
  // Expected Output:
  // [{
  //   mark: 'point',
  //   source: 'groupId.point.series1_id',
  //   encoding: {
  //     color: 'groupId.encoding.color',
  //     x: 'groupId.encoding.x'
  //     y: 'groupId.encoding.y0',
  //   },
  // },
  // {
  //   mark: 'bar',
  //   source: 'group.bar.series2_id',
  //   encoding: {
  //     color: 'groupId.encoding.color',
  //     text: {
  //       field: 'groupId.encoding.text1.field',
  //       rotation: 90,
  //     }
  //     x: 'groupId.encoding.x'
  //     y: 'groupId.encoding.y1',
  //   },
  // }]
  //
  const primaryChannel: string = encoding.marks.channel;
  const secondaryChannel = primaryChannel === 'x' ? 'y' : 'x';

  const idToMarkChannelMap = new Map<string, string>();
  return groupLayers.map((layer, index) => {
    const primaryChannelFieldId = layer.encoding[primaryChannel];
    if (typeof primaryChannelFieldId === 'string') {
      // The new layer has an encoding channel for the series field.
      // The id for the channel is sequential (ex: y0, y1, y2, etc.)
      const seriesChannel = `${primaryChannel}${index}`;
      channelToIdMap.set(seriesChannel, primaryChannelFieldId);
      // Create a source mapping for the layer (ex: 'bar_abcd' => 'abcd')
      // Since the series field is unique, the source id will be also.
      const source = `${layer.mark}_${primaryChannelFieldId}`;
      sourceMap.set(source, primaryChannelFieldId);

      // Make a deep copy of the layer so that we do not mutate the original.
      const memberLayer: MuzeLayer = deepCopy(layer);
      // Replace the source, primary and secondary channels with our new identifiers.
      memberLayer.source = `${groupId}.${source}`;
      memberLayer.encoding[primaryChannel] = `${groupId}.encoding.${seriesChannel}`;
      memberLayer.encoding[secondaryChannel] = `${groupId}.encoding.${secondaryChannel}`;

      // Check for baseline primary channel
      const baselineFieldId = layer.encoding[`${primaryChannel}0`];
      if (typeof baselineFieldId === 'string') {
        const baselineChannel = `${primaryChannel}0${index}`;
        channelToIdMap.set(baselineChannel, baselineFieldId);
        memberLayer.encoding[`${primaryChannel}0`] = `${groupId}.encoding.${baselineChannel}`;
      }

      // Do marks channels
      const setMarkChannelEncoding = (channel: MarkChannel) => {
        const encodingChannel = layer.encoding[channel];
        const fieldId = typeof encodingChannel === 'string' ? encodingChannel : encodingChannel?.field;

        if (fieldId) {
          // Check if this field is already defined
          let channelId = idToMarkChannelMap.get(fieldId);
          if (channelId === undefined) {
            channelId = `${channel}${index}`;
            idToMarkChannelMap.set(fieldId, channelId);
          }
          channelToIdMap.set(channelId, fieldId);
          if (typeof encodingChannel === 'string') {
            memberLayer.encoding[channel] = `${groupId}.encoding.${channelId}`;
          } else {
            (memberLayer.encoding[channel] as MuzeChannelEncoding).field = `${groupId}.encoding.${channelId}.field`;
          }
        }
      };

      setMarkChannelEncoding('color');
      setMarkChannelEncoding('size');
      setMarkChannelEncoding('text');
      setMarkChannelEncoding('detail');
      setMarkChannelEncoding('angle');

      return memberLayer;
    }
    return layer;
  });
}

function buildFieldConfig(fieldId: string, muzeSchema: MuzeDataSchema) {
  const muzeDataField = muzeSchema.find((item) => item.name === fieldId);
  return muzeDataField != null ? { field: fieldId, as: muzeDataField.as } : fieldId;
}

/**
 * Helper function to return field or summary field.
 *
 * @param {readonly Readonly<EncSeries>[]} allSeries all series
 * @param {MarkChannel} channel mark channel for the series
 * @param {MarksSummary} summary marks summary
 * @param {string|undefined} id id of field to be matched, or undefined for no match
 * @return {string|undefined} field or summary field, or undefined if no match found
 */
export function getFieldOrSummary(
  allSeries: readonly Readonly<EncSeries>[],
  channel: MarkChannel,
  summary: MarksSummary,
  id: string | undefined
): string | undefined {
  const summaryChannel = (summary as any)[`${channel}Ids`];
  const multiple: string[] = summaryChannel?.multiple ?? [];

  // If the given Id is found in the summary, it's a common field so return it...
  if (summary[channel]?.some((field) => field.id === id)) {
    return id;
  }

  // ...or if the id is one of the multiple fields, return the first matching field...
  else if (id && multiple?.includes(id)) {
    return getMultipleRefId(allSeries, channel, multiple, id);
  }

  // ...otherwise, see if it's in the channel Ids list and return the first element
  // which should be the common field or undefined if it's not in the summary anywhere.
  else {
    const summaryFields = Object.keys(summaryChannel);
    return summaryFields.find((summaryField) => {
      if (summaryField === 'multiple') {
        return false;
      }
      if (summaryChannel[summaryField].includes(id)) {
        return summaryChannel[summaryField][0];
      }
    });
  }
}

/**
 * Get the EncField id (or undefined) of the field in the specified channel.
 * The summary field id is returned when the same field is used across multiple
 * mark layers in the same channel.
 * When the channel field is the same as the field on the secondary axis, the
 * id of the secondary axis field is returned. This prevents an extra level
 * of detail from being added.
 */
export function getMarkChannelFieldId(
  encoding: Readonly<Encoding>,
  channel: MarkChannel,
  marks: Readonly<EncMarks>,
  summary: Readonly<MarksSummary>
) {
  const markChannelValues = marks[channel].values;
  if (markChannelValues.length === 0) {
    return undefined;
  }

  const channelFieldId = getFieldOrSummary(encoding.marks.series, channel, summary, markChannelValues[0].id);
  if (channelFieldId == null) {
    return channelFieldId;
  }

  const secondaryAxisField =
    encoding.marks.channel === '*' ? null : encoding.marks.channel === 'y' ? encoding.x[0] : encoding.y[0];

  if (secondaryAxisField == null) {
    return channelFieldId;
  }
  const encField = EncodingHelpers.getEncFieldById(encoding, channelFieldId);
  return encField.formula === secondaryAxisField.formula && encField.variable.type === secondaryAxisField.variable.type
    ? secondaryAxisField.id
    : encField.id;
}

function makeCommonChannelFields(
  marks: EncMarks,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  source: string,
  colors: ChartTypes.ChartColors,
  defaultColor: string | undefined
) {
  const channelEncodings: Record<string, string | MuzeChannelEncoding | undefined> = {};
  const summary: MarksSummary = EncodingHelpers.getSummary(encoding);

  const isBackgroundColor =
    EncodingHelpers.supportsColorAsBackground(encoding, encoding.marks.properties.color.values[0]) &&
    encoding.marks.properties.color?.target === 'background';
  const colorChannel = isBackgroundColor ? 'backgroundColor' : 'color';

  // add any mark values to the appropriate mark channel encoding
  if (marks.color.values?.length) {
    const markChannelFieldId = getMarkChannelFieldId(encoding, 'color', marks, summary);
    const encField =
      markChannelFieldId == null ? undefined : EncodingHelpers.getEncFieldById(encoding, markChannelFieldId);
    channelEncodings[colorChannel] = {
      field: markChannelFieldId,
      as: encField?.variable.type === VariableType.CONTINUOUS ? 'continuous' : 'discrete',
    };
  } else if (marks.color.value) {
    channelEncodings[colorChannel] = {
      value: () => marks.color.value,
    };
  } else {
    const encField = EncodingHelpers.getSeriesFieldById(encoding, source);
    if (encField) {
      const colorKey = getColorMapKeyByField(encField);
      if (colors.map?.[colorKey]) {
        channelEncodings[colorChannel] = {
          value: () => colors.map?.[colorKey],
        };
      }
    }
  }
  // Set the default color palette color if no colors were found
  if (!channelEncodings[colorChannel] && defaultColor !== undefined) {
    channelEncodings[colorChannel] = {
      value: () => defaultColor,
    };
  }

  if (marks.size.values?.length) {
    channelEncodings['size'] = getMarkChannelFieldId(encoding, 'size', marks, summary);
  }

  if (marks.text.values?.length) {
    const fieldId = getMarkChannelFieldId(encoding, 'text', marks, summary);
    const encField = fieldId && EncodingHelpers.getEncFieldById(encoding, fieldId);

    // Showing stack sums (ie totals on stacked bars) is only supported for very
    // specific encodings even though it is a general text encoding setting.
    const showStackSum = shouldShowStackSum(model, encoding, marks);

    channelEncodings['text'] = {
      field: fieldId,
      showStackSum,
    };
    if (encField) {
      const fields: FlamingoTypes.ModeField[] = [];
      let field = model.makeField(encField);
      if (field) {
        fields.push(field);

        const fieldKey = FieldFormatHelpers.getFormatMapKeyForModeField(field);
        const formats = fieldFormats[fieldKey];

        const labelFormat = formats?.paneFormat?.label;

        const rotation = labelFormat != null && Number.isFinite(labelFormat.rotation) ? labelFormat.rotation : 'auto';
        channelEncodings['text'] = {
          ...channelEncodings['text'],
          rotation,
        };
      }
      let measureNamesId = undefined;
      if (encField.formula === Values.Key && encoding.value.length) {
        // If we are dealing with Measure Values, save fields corresponding to
        // measure values to determine the right formatter.
        encoding.value.forEach((v) => {
          const valuesField = model.makeField(v);
          if (valuesField) {
            fields.push(valuesField);
          }
        });
        // Capture any Measure Names field that might be in the encoding to help match up the formatter.
        const measureNamesField = EncodingHelpers.findInEncoding(encoding, Names.Key);
        if (measureNamesField != null) {
          measureNamesId = measureNamesField.id;
        }
      }
      const formatter =
        fields.length > 1
          ? buildMuzeTextFormatter(model, fields, fieldFormats, measureNamesId)
          : getMuzeTextFormatter(model, encoding, field, fieldFormats);
      if (formatter) {
        channelEncodings['text'].formatter = formatter;
      }
      // For anything that is not a pivot table, we might apply filtering
      const textDisplay = EncodingHelpers.resolveTextDisplay(marks.text.display);
      if (encoding.marks.channel !== '*' && !textDisplay.showAll) {
        channelEncodings['text'].filter = makeTextFilter(textDisplay, source, encoding, marks, summary);
      }
      channelEncodings['text'].removeCollision = textDisplay.useLabelThinning;
    }
  } else {
    channelEncodings['text'] = {
      value: () => 'Abc',
      showStackSum: false,
    };
  }

  if (marks.detail.values?.length) {
    channelEncodings['detail'] = getMarkChannelFieldId(encoding, 'detail', marks, summary);
  }

  if (EncodingHelpers.supportsBaselineChannel(marks.type.value)) {
    const baselineFieldId = getMarkChannelFieldId(encoding, 'baseline', marks, summary);
    if (baselineFieldId) {
      switch (encoding.marks.channel) {
        case 'y':
          channelEncodings['y0'] = baselineFieldId;
          break;
        case 'x':
          channelEncodings['x0'] = baselineFieldId;
          break;
        default:
          // Baseline is not supported for tabular
          break;
      }
    }
  }

  return channelEncodings;
}

const COMMON_LAYER_CONFIG = {
  transition: { disabled: true },
  highlightExactPoint: true,
};

function makeBarLayer(
  channel: '*' | 'x' | 'y',
  source: string,
  marks: EncMarks,
  enc: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
): MuzeLayer {
  const mark = 'bar';

  const encoding = {
    ...makeCommonChannelFields(marks, enc, fieldFormats, model, source, colors, palette[0]),
  };

  const shouldUseFixedBarWidth = (encField: EncField) => {
    if (!encField.id) {
      return false;
    }
    const compiledField = model.compileEncField(encField);
    if (!compiledField.isValid) {
      return false;
    }
    return (
      compiledField.isContinuous &&
      !compiledField.hasDateFunc &&
      !compiledField.isAttrDateType &&
      !compiledField.isFormulaDate &&
      !compiledField.isAggregateStage
    );
  };

  const shouldUseStackBy = (encField: EncField) => {
    if (!encField.id) {
      return false;
    }
    const compiledField = model.compileEncField(encField);
    if (!compiledField.isValid) {
      return false;
    }
    return compiledField.isContinuous && !compiledField.isBinned;
  };

  let useFixedBarWidth = false;
  let isStackByAllowed = true;

  switch (channel) {
    case '*': {
      break;
    }
    case 'y': {
      encoding['y'] = source;
      useFixedBarWidth = [...enc.x, ...enc.x2].some((f) => shouldUseFixedBarWidth(f));
      isStackByAllowed = [...enc.y, ...enc.y2].some((f) => shouldUseStackBy(f));
      break;
    }
    case 'x': {
      encoding['x'] = source;
      useFixedBarWidth = [...enc.y, ...enc.y2].some((f) => shouldUseFixedBarWidth(f));
      isStackByAllowed = [...enc.x, ...enc.x2].some((f) => shouldUseStackBy(f));
      break;
    }
  }
  if (useFixedBarWidth) {
    // NOTE: Currently setting a size field with a continuous axis has no effect. Preserving the field setting
    // anyway in case this changes in the future. Since Muze doesn't support, don't know which will take precedence.
    const sizeField = encoding['size'] as string;
    encoding['size'] = {
      field: sizeField,
      value: DEFAULT_MIN_BAR_WIDTH,
    };
  }

  // Determine if there is a transform applied to the bars. This isn't applicable to a tabular bar.
  // With the exception of 'Stack100Percent', the transform is only applicable if there is additional
  // grouping applied to the layer due to a discrete mark channel.
  //
  const options = (marks.options?.[MarkType.Bar] ?? { transform: undefined }) as BarMarkOptions;
  const transform =
    enc.marks.channel !== '*' &&
    (options.transform === MarkTransform.Stack100Percent || hasDiscreteMarkChannel(marks)) &&
    isStackByAllowed
      ? options.transform
      : undefined;

  return {
    mark,
    source,
    encoding,
    ...COMMON_LAYER_CONFIG,
    highlightExactPoint: useHighlightExactPoint(enc, encoding),
    transform: transform !== undefined ? { type: transform } : undefined,
  };
}

function makeLineLayer(
  channel: '*' | 'x' | 'y',
  source: string,
  marks: EncMarks,
  enc: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
): MuzeLayer {
  const mark = 'line';

  const encoding = {
    ...makeCommonChannelFields(marks, enc, fieldFormats, model, source, colors, palette[0]),
  };
  switch (channel) {
    case '*': {
      break;
    }
    case 'y': {
      encoding['y'] = source;
      break;
    }
    case 'x': {
      encoding['x'] = source;
      break;
    }
  }

  const options = (marks.options?.[MarkType.Line] ?? {}) as LineMarkOptions;
  const connectNullData = options.nullTreatment === MarkNullTreatment.ConnectDashed ?? false;
  const nullDataLineStyle =
    options.nullTreatment === MarkNullTreatment.ConnectDashed ? { 'stroke-dasharray': '5 3' } : undefined;

  const transform =
    enc.marks.channel !== '*' && hasDiscreteMarkChannel(marks)
      ? resolveLineTransform(enc, marks, options.transform)
      : undefined;

  return {
    mark,
    source,
    encoding,
    ...COMMON_LAYER_CONFIG,
    highlightExactPoint: useHighlightExactPoint(enc, encoding),
    interpolate: options.interpolation ?? MarkInterpolation.Linear,
    connectNullData,
    nullDataLineStyle,
    transform: transform !== undefined ? { type: transform } : undefined,
  };
}

function makeAreaLayer(
  channel: '*' | 'x' | 'y',
  source: string,
  marks: EncMarks,
  enc: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
): MuzeLayer {
  const mark = 'area';

  const encoding = {
    ...makeCommonChannelFields(marks, enc, fieldFormats, model, source, colors, palette[0]),
  };
  switch (channel) {
    case '*': {
      break;
    }
    case 'y': {
      encoding['y'] = source;
      break;
    }
    case 'x': {
      encoding['x'] = source;
      break;
    }
  }

  const options = (marks.options?.[MarkType.Area] ?? {}) as AreaMarkOptions;
  const transform = enc.marks.channel !== '*' && hasDiscreteMarkChannel(marks) ? options.transform : undefined;

  return {
    mark,
    source,
    encoding,
    ...COMMON_LAYER_CONFIG,
    highlightExactPoint: useHighlightExactPoint(enc, encoding),
    interpolate: options.interpolation ?? MarkInterpolation.Linear,
    transform: transform !== undefined ? { type: transform } : {},
  };
}

function makeCircleLayer(
  channel: '*' | 'x' | 'y',
  source: string,
  marks: EncMarks,
  enc: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
): MuzeLayer {
  const mark = 'point';

  const encoding = {
    ...makeCommonChannelFields(marks, enc, fieldFormats, model, source, colors, palette[0]),
  };
  switch (channel) {
    case '*': {
      break;
    }
    case 'y': {
      encoding['y'] = source;
      break;
    }
    case 'x': {
      encoding['x'] = source;
      break;
    }
  }

  return {
    mark,
    source,
    encoding,
    ...COMMON_LAYER_CONFIG,
  };
}

function makeShapeLayer(
  channel: '*' | 'x' | 'y',
  source: string,
  marks: EncMarks,
  enc: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
): MuzeLayer {
  const mark = 'point';

  const encoding = {
    ...makeCommonChannelFields(marks, enc, fieldFormats, model, source, colors, palette[0]),
  };
  switch (channel) {
    case '*': {
      break;
    }
    case 'y': {
      encoding['y'] = source;
      break;
    }
    case 'x': {
      encoding['x'] = source;
      break;
    }
  }

  return {
    mark,
    source,
    encoding,
    ...COMMON_LAYER_CONFIG,
  };
}

function makeTextLayer(
  channel: '*' | 'x' | 'y',
  source: string,
  marks: EncMarks,
  enc: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
): MuzeLayer {
  const mark = 'text';

  const textColor = !isTextTable(enc) ? palette[0] : undefined;
  const encoding = {
    ...makeCommonChannelFields(marks, enc, fieldFormats, model, source, colors, textColor),
  };
  switch (channel) {
    case '*': {
      break;
    }
    case 'y': {
      encoding['y'] = source;
      break;
    }
    case 'x': {
      encoding['x'] = source;
      break;
    }
  }

  return {
    mark,
    source,
    encoding,
    ...COMMON_LAYER_CONFIG,
  };
}

function makePieLayer(
  channel: '*' | 'x' | 'y',
  source: string,
  marks: EncMarks,
  enc: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
): MuzeLayer {
  const mark = 'arc';

  const encoding = {
    ...makeCommonChannelFields(marks, enc, fieldFormats, model, source, colors, palette[0]),
  };
  switch (channel) {
    case '*': {
      break;
    }
    case 'y': {
      encoding['y'] = source;
      break;
    }
    case 'x': {
      encoding['x'] = source;
      break;
    }
  }

  if (marks.angle.values?.length) {
    encoding['angle'] = getFieldOrSummary(
      enc.marks.series,
      'angle',
      EncodingHelpers.getSummary(enc),
      marks.angle.values[0].id
    );
  }

  return {
    mark,
    source,
    encoding,
    ...COMMON_LAYER_CONFIG,
  };
}

function makeDonutLayer(
  channel: '*' | 'x' | 'y',
  source: string,
  marks: EncMarks,
  enc: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  model: RenderDataModel,
  colors: ChartTypes.ChartColors,
  palette: readonly string[]
): MuzeLayer {
  // Currently Donut is just a Pie with an inner radius.
  const layer = makePieLayer(channel, source, marks, enc, fieldFormats, model, colors, palette);

  layer.encoding['radius'] = {
    range: (range: number[]) => [0.5 * (range[1] - range[0]), range[1]],
  };
  return layer;
}

function hasDiscreteMarkChannel(marks: EncMarks): boolean {
  const markChannelFields = [
    ...marks.color.values,
    ...marks.size.values,
    ...marks.text.values,
    ...marks.detail.values,
    ...marks.angle.values,
  ];
  if (markChannelFields.length === 0) {
    return false;
  }
  return markChannelFields.some((field) => {
    return field.variable.type === VariableType.DISCRETE;
  });
}

function useHighlightExactPoint(
  encoding: Encoding,
  muzeEncoding: Record<string, string | MuzeChannelEncoding | undefined>
) {
  // 'highlightExactPoint' controls whether Muze sends data for all of the segments
  // of the stacked bar/line/area or just the exact segment you are hovering over.
  // For a stacked bar/line/area, we show all of the segment data in the tooltip
  // so we need 'highlightExactPoint' to be false.
  // The performance is significantly worse when set to 'false' so we only want to set
  // it to 'false' when there is a discrete color or detail.
  const highlightForChannel = (channel: string) => {
    const channelValue = muzeEncoding[channel];
    if (typeof channelValue !== 'string') {
      // If channelValue is a MuzeChannelEncoding object with an as set to discrete, return false.
      if (channelValue != null && channelValue.as === VariableType.DISCRETE.toLowerCase()) {
        return false;
      } else {
        return true;
      }
    }
    const encField = EncodingHelpers.getEncFieldById(encoding, channelValue);
    if (!encField) {
      return true;
    }
    // Return 'false' (ie return all segments) if DISCRETE
    return encField.variable.type !== VariableType.DISCRETE;
  };
  // Return 'false' if there is either discrete color or discrete detail
  return highlightForChannel('color') && highlightForChannel('detail');
}
/**
 * Determines if showStackSum is enabled or not. To return true, the encoding must
 * support showing stack sums and the setting must be enabled.
 */
export function shouldShowStackSum(model: RenderDataModel, encoding: Encoding, marks: EncMarks) {
  const barOptions = (marks.options?.[MarkType.Bar] ?? {
    type: MarkType.Bar,
    transform: MarkTransform.Stack,
    showStackSum: false,
  }) as BarMarkOptions;

  // showStackSum must be enabled
  if (!barOptions.showStackSum) {
    return false;
  }

  // Even if it is enabled, the current encoding may not support it.
  if (!supportsShowStackSum(model, encoding, marks, barOptions)) {
    return false;
  }
  // Otherwise, allow it.
  return true;
}

/**
 * Determines if the encoding could support showing bar labels as totals at the top of the bar.
 * It does not indicate that the option is enabled or not, only that it could be enabled.
 */
export function supportsShowStackSum(
  model: RenderDataModel,
  encoding: Encoding,
  marks: EncMarks,
  barOptions: BarMarkOptions
) {
  // Only supported for bar and for x|y encoding
  if (marks.type.value !== MarkType.Bar || encoding.marks.channel === '*') {
    return false;
  }
  // It must be a stack transform. Stack is the default when undefined so we only
  // need to check if it is explicitly not Stack.
  if (
    barOptions.transform !== undefined &&
    barOptions.transform !== MarkTransform.Stack &&
    barOptions.transform !== MarkTransform.Stack100Percent
  ) {
    return false;
  }
  // There must be at least one discrete field on a marks channel
  if (!hasDiscreteMarkChannel(marks)) {
    return false;
  }
  // There must be a text channel...
  if (marks.text.values.length < 1) {
    return false;
  }
  // ...and it must be continuous measure. Muze will only stack a field with a 'type'='measure' as
  // defined in buildMuzeDataField.
  const modeTextField = model.makeField(marks.text.values[0]);
  if (modeTextField?.type !== Attr.Type.Continuous || modeTextField?.role === Attr.Role.Dimension) {
    return false;
  }
  // Passes all the checks
  return true;
}

export function resolveLineTransform(
  encoding: Encoding,
  marks: EncMarks,
  candidateTransform: MarkTransform | undefined
) {
  // Not all transforms are supported for all encodings. Check the candidate transform and return it
  // if it is supported. Otherwise, fallback to a supported transform.
  // If there is a mark channel field with the same formula as the secondary axis field, then
  // only Identity is supported.
  // 100% stacked is not supported.
  // Group is supported in all encodings. If it is set to Group or not defined, return Group.

  if (encoding.marks.channel === '*') {
    // No transform for a tabular layout
    return MarkTransform.Group;
  }

  const secondaryAxisField = encoding.marks.channel === 'y' ? encoding.x[0] : encoding.y[0];
  // First check if this is an identity encoding.
  if (secondaryAxisField !== undefined) {
    const discreteChannelValues = [
      ...marks.color.values,
      ...marks.size.values,
      ...marks.text.values,
      ...marks.detail.values,
    ].filter((field) => field.variable.type === VariableType.DISCRETE || field.formula === secondaryAxisField.formula);

    if (
      discreteChannelValues.length > 0 &&
      discreteChannelValues.every((field) => field.formula === secondaryAxisField.formula)
    ) {
      return MarkTransform.Identity;
    }
  }

  if (
    candidateTransform === undefined ||
    candidateTransform === MarkTransform.Group ||
    candidateTransform === MarkTransform.Stack100Percent
  ) {
    return MarkTransform.Group;
  }

  return candidateTransform;
}

type RowIndexFn = (dataModel: any, source: string) => any;

export function makeTextFilter(
  textDisplay: Required<TextDisplay>,
  source: string,
  encoding: Encoding,
  marks: EncMarks,
  summary: MarksSummary
) {
  const showAll = textDisplay.showAll;
  const showFirst = textDisplay.showFirst;
  const showLast = textDisplay.showLast;
  const showMax = textDisplay.showMax;
  const showMin = textDisplay.showMin;

  if ([showAll, showFirst, showLast, showMax, showMin].every((flag) => !flag)) {
    // If all of the flags are false, show no labels
    return makeShowNoneFilter();
  }

  const series = encoding.marks.series;
  // Capture all of the discrete fields which are used to select/partition the
  // Muze data set.
  const discreteFieldIds: Array<string> = [];
  const addFieldIds = (channel: MarkChannel, fields: Readonly<EncField[]>) => {
    fields.forEach((encField) => {
      if (encField.variable.type === VariableType.DISCRETE) {
        const fieldOrSummaryId = getFieldOrSummary(series, channel, summary, encField.id);
        if (fieldOrSummaryId != null) {
          discreteFieldIds.push(fieldOrSummaryId);
        }
      }
    });
  };
  addFieldIds('angle', marks.angle.values);
  addFieldIds('color', marks.color.values);
  addFieldIds('detail', marks.detail.values);
  addFieldIds('label', marks.label.values);
  addFieldIds('size', marks.size.values);

  // Only add the text channel if it is different from the secondary axis field. If they
  // are the same and we add it, then the partitioning of the data set will result in
  // a single row (ie label every point) which probably is not what the user wants.
  const secondaryAxis = encoding.marks.channel === 'y' ? encoding.x[0] : encoding.y[0];
  if (secondaryAxis?.formula !== marks.text.values[0]?.formula) {
    addFieldIds('text', marks.text.values);
  }

  // Add the columns and rows for the facets.
  encoding.column.forEach((field) => {
    if (field.id != null && field.variable.type === VariableType.DISCRETE) {
      discreteFieldIds.push(field.id);
    }
  });
  encoding.row.forEach((field) => {
    if (field.id != null && field.variable.type === VariableType.DISCRETE) {
      discreteFieldIds.push(field.id);
    }
  });

  const showFirstFilter = makeFilterFunction(discreteFieldIds, source, firstRowFunc);
  const showLastFilter = makeFilterFunction(discreteFieldIds, source, lastRowFunc);
  const showMaxFilter = makeFilterFunction(discreteFieldIds, source, maxRowFunc);
  const showMinFilter = makeFilterFunction(discreteFieldIds, source, minRowFunc);

  // Return a function which checks against the filter functions
  return function textFilter(dataInfo: MuzeTextDataInfo, contextInfo: MuzeContextInfo) {
    return (
      (showFirst && showFirstFilter(dataInfo, contextInfo)) ||
      (showLast && showLastFilter(dataInfo, contextInfo)) ||
      (showMax && showMaxFilter(dataInfo, contextInfo)) ||
      (showMin && showMinFilter(dataInfo, contextInfo))
    );
  };
}

/**
 * Returns the row index from the '__id__' column which corresponds to the first
 * item in the series
 */
function firstRowFunc(dataModel: any, series: string) {
  const seriesData = dataModel.getData();
  const rowIdIndex = seriesData.schema.findIndex((item: any) => item.name === '__id__');
  return rowIdIndex >= 0 && seriesData.data.length > 0 ? seriesData.data[0][rowIdIndex] : -1;
}

/**
 * Returns the row index from the '__id__' column which corresponds to the last
 * item in the series
 */
function lastRowFunc(dataModel: any, series: string) {
  const seriesData = dataModel.getData();
  const rowIdIndex = seriesData.schema.findIndex((item: any) => item.name === '__id__');
  return rowIdIndex >= 0 && seriesData.data.length > 0 ? seriesData.data[seriesData.data.length - 1][rowIdIndex] : -1;
}

/**
 * Returns the row index from the '__id__' column which corresponds to the maximum
 * value of the source field in the series data.
 */
function maxRowFunc(dataModel: any, series: string) {
  const sortedDataModel = dataModel.sort([[series, 'desc']]);
  const seriesData = sortedDataModel.getData();
  const rowIdIndex = seriesData.schema.findIndex((item: any) => item.name === '__id__');
  return rowIdIndex >= 0 && seriesData.data.length > 0 ? seriesData.data[0][rowIdIndex] : -1;
}

/**
 * Returns the row index from the '__id__' column which corresponds to the minimum
 * value of the source field in the series data.
 */
function minRowFunc(dataModel: any, series: string) {
  const sortedDataModel = dataModel.sort([[series, 'asc']]);
  const seriesData = sortedDataModel.getData();
  const rowIdIndex = seriesData.schema.findIndex((item: any) => item.name === '__id__');
  return rowIdIndex >= 0 && seriesData.data.length > 0 ? seriesData.data[0][rowIdIndex] : -1;
}

/**
 * Builds the filter function which determines if a particular text should be displayed
 * based on the criteria in the RowIndexFn.
 * This function handles common tasks such as:
 *   - Selecting the rows within the facet/series in the data/context passed from Muze
 *   - Determining if the passed row index matches the row index which should be displayed
 *   - Caching the row index for the particular facet/series combination
 *
 * This function calls out to the specified RowIndexFn to determine the row index
 * that matches the filter expectation.
 *
 * @param discreteFieldIds - All discrete fields in the encoding that specify the partitioning of the data
 * @param series - Id of the field in the source series
 * @param rowIndexFunc - Function which determines the row index matching the particular filter type
 */
function makeFilterFunction(discreteFieldIds: Array<string>, series: string, rowIndexFunc: RowIndexFn) {
  const cache = new Map<string, number>();

  return function filterFn(dataInfo: MuzeTextDataInfo, contextInfo: MuzeContextInfo) {
    if (dataInfo?.rowData != null) {
      const rowData = dataInfo.rowData;
      const key = discreteFieldIds
        .map((fieldId) => {
          return rowData[fieldId];
        })
        .join('+');

      if (cache.has(key)) {
        const rowIndex = cache.get(key);
        return rowIndex === rowData['__id__'];
      } else {
        // Keep track of all of the child data models that we create so
        // we can dispose of them. We do not want to dispose of the
        // original data model that we get from the contextInfo.
        const createdDataModels = [];
        let dm = contextInfo.context.data();
        discreteFieldIds.forEach((fieldId) => {
          const selection = {
            value: FieldFormatHelpers.isMissingValue(rowData[fieldId]) ? 'null' : rowData[fieldId],
            field: fieldId,
            operator: 'eq',
          };
          dm = dm.select(selection);
          createdDataModels.push(dm);
        });
        // Filter out any null series values
        dm = dm.select({ value: 'null', field: series, operator: 'neq' });
        createdDataModels.push(dm);
        const rowIndex = rowIndexFunc(dm, series);

        // Clean up the data models once we have the index
        createdDataModels.forEach((dataModel) => dataModel.dispose());
        if (rowIndex >= 0) {
          cache.set(key, rowIndex);
          return rowIndex === rowData['__id__'];
        }
      }
    }
    return true;
  };
}

/**
 * Generic function which hides all text
 */
function makeShowNoneFilter() {
  return function noneFilter() {
    return false;
  };
}
